import {useEffect, useRef, useState, type FormEvent, type RefObject} from "react";
import {useTranslation} from "react-i18next";
import {
  ApiError,
  OperationKey,
  completePasswordReset,
  requestPasswordReset,
  type PasswordResetCompleteInput,
  type PasswordResetRequestInput,
  type CaptchaResponse,
} from "../../../shared/api/client";
import {localizeAPIError, type ErrorContext} from "../../../shared/i18n/api-errors";
import {sessionStore} from "../../../shared/session/session-store";
import {Field} from "../../../shared/ui/field";
import {CaptchaChallenge, captchaChallengeFromError, type CaptchaChallengeDescriptor} from "./captcha-challenge";

export interface PasswordResetAPI {
  request(input: PasswordResetRequestInput, operation: OperationKey): Promise<void>;
  complete(input: PasswordResetCompleteInput, operation: OperationKey): Promise<void>;
}

interface PasswordResetProps {
  emailToken?: string;
  api?: PasswordResetAPI;
  onCompleted?: () => void;
  redirectDelayMs?: number;
}

type Stage = "request" | "requested" | "email" | "phone" | "complete";

const defaultAPI: PasswordResetAPI = {
  request: requestPasswordReset,
  complete: completePasswordReset,
};

export function PasswordReset({
  emailToken,
  api = defaultAPI,
  onCompleted = redirectToSignin,
  redirectDelayMs = 1_200,
}: PasswordResetProps) {
  const {t} = useTranslation();
  const [stage, setStage] = useState<Stage>(emailToken === undefined ? "request" : "email");
  const [login, setLogin] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [token, setToken] = useState(emailToken ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallengeDescriptor | null>(null);
  const [captchaResponse, setCaptchaResponse] = useState<CaptchaResponse | null>(null);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const [errorContext, setErrorContext] = useState<ErrorContext>("password-reset-request");
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const missingTokenRef = useRef<HTMLDivElement>(null);
  const localizedError = error ? localizeAPIError(error, errorContext, t) : null;

  useEffect(() => {
    if (!error) return;
    const frame = requestAnimationFrame(() => {
      const invalid = formRef.current?.querySelector<HTMLElement>("[aria-invalid='true']");
      (invalid ?? errorRef.current)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [error]);

  useEffect(() => {
    if (stage !== "email" || token) return;
    const frame = requestAnimationFrame(() => missingTokenRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [stage, token]);

  useEffect(() => {
    if (stage !== "complete") return;
    const timer = window.setTimeout(onCompleted, redirectDelayMs);
    return () => window.clearTimeout(timer);
  }, [onCompleted, redirectDelayMs, stage]);

  const requestReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const nextLogin = text(form, "login");
    if (!nextLogin) {
      setErrorContext("password-reset-request");
      setError(fieldError("login", "client_required"));
      return;
    }

    setBusy(true);
    setError(null);
    setErrorContext("password-reset-request");
    try {
      await api.request({login: nextLogin}, new OperationKey());
      if (isE164Phone(nextLogin)) {
        setLogin(nextLogin);
        setStage("phone");
      } else {
        setLogin("");
        setStage("requested");
      }
    } catch (caught) {
      setError(asAPIError(caught));
    } finally {
      setBusy(false);
    }
  };

  const completeReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorContext("password-reset-complete");
    setError(null);
    const submittedCaptcha = Boolean(captchaResponse);
    const clearSubmittedCaptcha = () => {
      if (!submittedCaptcha) return;
      setCaptchaResponse(null);
      setCaptchaAttempt((value) => value + 1);
    };

    if (!newPassword) {
      setError(fieldError("new_password", "client_required"));
      clearSubmittedCaptcha();
      return;
    }
    if (newPassword !== passwordConfirmation) {
      setError(fieldError("password_confirmation", "client_password_mismatch"));
      clearSubmittedCaptcha();
      return;
    }
    if (stage === "phone" && !/^[0-9]{6}$/.test(code)) {
      setError(fieldError("code", "client_otp_format"));
      clearSubmittedCaptcha();
      return;
    }
    if (stage === "email" && !token) {
      setError(new ApiError(422, errorEnvelope("password_reset_invalid")));
      return;
    }
    if (stage === "phone" && captchaChallenge && !captchaResponse) return;

    const input: PasswordResetCompleteInput = stage === "phone"
      ? {login, code, new_password: newPassword, ...(captchaResponse ? {captcha: captchaResponse} : {})}
      : {token, new_password: newPassword};
    setBusy(true);
    try {
      // Each explicit proof submission is a new logical attempt. The API
      // helper keeps this operation key only for its transport retry.
      await api.complete(input, new OperationKey());
      setToken("");
      setLogin("");
      setCode("");
      setNewPassword("");
      setPasswordConfirmation("");
      setCaptchaResponse(null);
      setCaptchaChallenge(null);
      sessionStore.clear();
      setStage("complete");
    } catch (caught) {
      const nextError = asAPIError(caught);
      const nextChallenge = captchaChallengeFromError(nextError);
      if (nextChallenge) setCaptchaChallenge(nextChallenge);
      setError(nextError);
      setCode("");
    } finally {
      clearSubmittedCaptcha();
      setBusy(false);
    }
  };

  const startOver = () => {
    setToken("");
    setLogin("");
    setCode("");
    setNewPassword("");
    setPasswordConfirmation("");
    setCaptchaResponse(null);
    setCaptchaChallenge(null);
    setCaptchaAttempt((value) => value + 1);
    setError(null);
    setStage("request");
  };

  if (stage === "complete") {
    return <section className="auth-complete" aria-live="polite" aria-atomic="true">
      <h1>{t("auth.passwordReset.successTitle")}</h1>
      <p>{t("auth.passwordReset.successBody")}</p>
      <a className="button primary" href="/signin?password_reset=1">{t("auth.passwordReset.goToSignin")}</a>
    </section>;
  }

  if (stage === "requested") {
    return <section className="auth-complete" aria-live="polite" aria-atomic="true">
      <h1>{t("auth.passwordReset.requestedTitle")}</h1>
      <p>{t("auth.passwordReset.requestedBody")}</p>
      <div className="password-reset-actions">
        <button className="button secondary" type="button" onClick={startOver}>{t("auth.passwordReset.requestAgain")}</button>
        <a className="button primary" href="/signin">{t("auth.passwordReset.backToSignin")}</a>
      </div>
    </section>;
  }

  if (stage === "request") {
    return <section className="auth-card password-reset">
      <h1>{t("auth.passwordReset.requestTitle")}</h1>
      <p>{t("auth.passwordReset.requestBody")}</p>
      <form ref={formRef} onSubmit={requestReset} noValidate aria-busy={busy}>
        <Field name="login" label={t("auth.login")} autoComplete="username" maxLength={320} required autoFocus error={localizedError?.fields.login} />
        {localizedError && <ResetError error={localizedError} errorRef={errorRef} />}
        <button className="button primary" type="submit" disabled={busy}>{busy ? t("auth.passwordReset.requesting") : t("auth.passwordReset.request")}</button>
        <a className="auth-secondary-link" href="/signin">{t("auth.passwordReset.backToSignin")}</a>
      </form>
    </section>;
  }

  if (stage === "email" && !token) {
    return <section className="auth-card password-reset">
      <h1>{t("auth.passwordReset.completeTitle")}</h1>
      <div className="form-error" role="alert" ref={missingTokenRef} tabIndex={-1}>
        <p>{t("errors.passwordResetInvalid")}</p>
      </div>
      <a className="button primary" href="/forgot-password">{t("auth.passwordReset.requestAgain")}</a>
    </section>;
  }

  return <section className="auth-card password-reset">
    <h1>{stage === "phone" ? t("auth.passwordReset.phoneTitle") : t("auth.passwordReset.completeTitle")}</h1>
    <p>{stage === "phone" ? t("auth.passwordReset.requestedBody") : t("auth.passwordReset.completeBody")}</p>
    <form ref={formRef} onSubmit={completeReset} noValidate aria-busy={busy}>
      {stage === "phone" && <>
        <Field name="login" type="tel" label={t("auth.phoneLabel")} value={login} readOnly />
        <Field
          name="code"
          label={t("auth.passwordReset.code")}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          minLength={6}
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
          required
          autoFocus
          error={localizedError?.fields.code}
        />
      </>}
      <Field
        name="new_password"
        type="password"
        label={t("auth.passwordReset.newPassword")}
        autoComplete="new-password"
        minLength={8}
        maxLength={128}
        value={newPassword}
        onChange={(event) => setNewPassword(event.currentTarget.value)}
        required
        autoFocus={stage === "email"}
        error={localizedError?.fields.new_password}
      />
      <Field
        name="password_confirmation"
        type="password"
        label={t("auth.passwordReset.passwordConfirmation")}
        autoComplete="new-password"
        minLength={8}
        maxLength={128}
        value={passwordConfirmation}
        onChange={(event) => setPasswordConfirmation(event.currentTarget.value)}
        required
        error={localizedError?.fields.password_confirmation}
      />
      {stage === "phone" && captchaChallenge && <CaptchaChallenge
        challenge={captchaChallenge}
        attempt={captchaAttempt}
        onResponse={setCaptchaResponse}
      />}
      {localizedError && <ResetError error={localizedError} errorRef={errorRef} />}
      <button className="button primary" type="submit" disabled={busy || (stage === "phone" && Boolean(captchaChallenge) && !captchaResponse)}>{busy ? t("auth.passwordReset.completing") : t("auth.passwordReset.complete")}</button>
      {stage === "phone" && <button className="button secondary" type="button" disabled={busy} onClick={startOver}>{t("auth.passwordReset.resend")}</button>}
    </form>
  </section>;
}

function ResetError({error, errorRef}: {
  error: ReturnType<typeof localizeAPIError>;
  errorRef: RefObject<HTMLDivElement | null>;
}) {
  const {t} = useTranslation();
  return <div className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
    <p>{error.message}</p>
    {error.requestId && <small>{t("common.requestId")}: <code>{error.requestId}</code></small>}
  </div>;
}

function isE164Phone(value: string): boolean {
  return /^\+[1-9][0-9]{7,14}$/.test(value);
}

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value.trim() : "";
}

function fieldError(name: string, code: string): ApiError {
  return new ApiError(422, errorEnvelope(code, {[name]: code}));
}

function asAPIError(caught: unknown): ApiError {
  return caught instanceof ApiError ? caught : new ApiError(0, errorEnvelope("client_error"));
}

function errorEnvelope(code: string, fields: Record<string, string> = {}) {
  return {error: {code, message: "", request_id: "", fields}};
}

function redirectToSignin(): void {
  window.location.assign("/signin?password_reset=1");
}

export function takePasswordResetToken(): string {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  if (window.location.search) {
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.hash}`);
  }
  return token;
}
